import { useEffect, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'

import { getInventoryGroupChildren, type InventoryGroupFilters } from '@/lib/api/client/inventory'
import { guardReasonLabel } from '@/lib/stock-guard-format'
import type { GroupStockConflict, InventoryGroupChildLevel, InventoryGroupChildren, InventoryGroupPage, InventoryGroupRow, InventoryGroupUnit } from '@/lib/api/schemas/inventory.schema'
import type { InventoryRow } from '@/lib/api/schemas/inventory.schema'
import { freshnessLabels, inventoryReviewStateLabels, inventoryRowGrainLabels, inventoryStockStateLabels, unitReasonLabels, warehouseDateTime } from '@/lib/warehouse/labels'
import { WarehouseThumbnail } from '@/components/warehouse/WarehouseThumbnail'
import type { WarehouseSelection } from '@/hooks/useWarehouseSelection'
import type { WarehouseSort, WarehouseSortColumn } from '@/lib/warehouse/url-state'
import { formatRubleAmount } from '@/lib/market-money'
import { SelectAllCheckbox } from '@/components/warehouse/SelectAllCheckbox'
import { INVENTORY_COLUMN_REGISTRY, ORDER_GROUP_DEFAULT_VISIBLE_COLUMNS, ProblemsCell, type InventoryColumnId } from '@/lib/views/columns/inventory'
import { ValueOrReason } from '@/components/warehouse/primitives'

function grams(value: number | null): string { return value === null ? '—' : value.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) }

function weightValue(value: number | null, reason: string | null): ReactNode {
  return <ValueOrReason value={value === null ? null : grams(value)} reason={reason} />
}

const childLevelNouns: Record<InventoryGroupChildLevel, readonly [string, string, string]> = {
  article: ['артикул', 'артикула', 'артикулов'],
  articleSize: ['размер', 'размера', 'размеров'],
  unit: ['изделие', 'изделия', 'изделий'],
}

const childLevelActionNouns: Record<InventoryGroupChildLevel, readonly [string, string, string]> = {
  article: ['артикул', 'артикула', 'артикулов'],
  articleSize: ['размер', 'размера', 'размеров'],
  unit: ['изделие', 'изделия', 'изделий'],
}

function pluralForm(value: number): 0 | 1 | 2 {
  const remainder10 = value % 10
  const remainder100 = value % 100
  if (remainder10 === 1 && remainder100 !== 11) return 0
  if (remainder10 >= 2 && remainder10 <= 4 && (remainder100 < 12 || remainder100 > 14)) return 1
  return 2
}

function populationCount(value: number, childLevel: InventoryGroupChildLevel): string {
  return `${value.toLocaleString('ru-RU')} ${childLevelNouns[childLevel][pluralForm(value)]}`
}

function productCount(value: number): string {
  const forms = ['товар', 'товара', 'товаров'] as const
  return `${value.toLocaleString('ru-RU')} ${forms[pluralForm(value)]}`
}

function actionPopulationCount(value: number, childLevel: InventoryGroupChildLevel): string {
  return `${value.toLocaleString('ru-RU')} ${childLevelActionNouns[childLevel][pluralForm(value)]}`
}

// A reconciliation gap, never a stock. The count above is what we can stand
// behind; this says what the source claims and how much of that claim has no
// named piece behind it. The two numbers are never summed.
function stockConflictLine(conflict: GroupStockConflict | null | undefined): ReactNode {
  if (!conflict) return null
  return <small className="block text-st-unpaid" data-testid="group-stock-conflict" data-conflict-kind={conflict.kind}>1С: {conflict.sourceQuantity.toLocaleString('ru-RU')} · не опознано: {conflict.unnamedQuantity.toLocaleString('ru-RU')}</small>
}

function directProductParent(row: InventoryGroupRow): boolean {
  return row.childLevel === 'unit' && (row.level === 'model' || row.level === 'article')
}

function childPopulationCount(row: InventoryGroupRow): string {
  return directProductParent(row)
    ? productCount(row.childCount)
    : populationCount(row.childCount, row.childLevel)
}

function childActionPopulationCount(row: InventoryGroupRow): string {
  return directProductParent(row)
    ? productCount(row.childCount)
    : actionPopulationCount(row.childCount, row.childLevel)
}

function reasonText(code: string): string {
  return unitReasonLabels[code] ?? guardReasonLabel(code)
}

function orderColumns(visibleColumns: readonly InventoryColumnId[] | undefined) {
  const hasOrderColumn = visibleColumns?.some((id) => INVENTORY_COLUMN_REGISTRY.find((candidate) => candidate.id === id)?.facets.includes('order')) ?? false
  const selected = hasOrderColumn && visibleColumns !== undefined ? visibleColumns : ORDER_GROUP_DEFAULT_VISIBLE_COLUMNS
  return selected.flatMap((id) => {
    const column = INVENTORY_COLUMN_REGISTRY.find((candidate) => candidate.id === id)
    return column?.groupCell === undefined ? [] : [column]
  })
}

function orderRowLabel(row: InventoryGroupRow): string {
  return row.orderNumber ? `Открыть заказ №${row.orderNumber}` : 'Открыть заказ'
}

function pieceWeight(row: InventoryRow): ReactNode {
  return <ValueOrReason value={row.weightGrams === null ? null : grams(row.weightGrams)} reason={row.weightUnavailableReason} suffix="г" />
}

function groupMediaCounts(row: InventoryGroupRow): ReactNode {
  return <span data-testid="group-media-counts"><span data-photo-count={row.photoCount}><ValueOrReason value={row.photoCount} reason={null} /> фото</span> · <span data-video-count={row.videoCount}><ValueOrReason value={row.videoCount} reason={null} /> видео</span></span>
}

function groupProblems(row: InventoryGroupRow): ReactNode {
  return <ProblemsCell problems={row.problems ?? []} problemsAvailable={row.problemsAvailable} />
}

function OrderPieceRow({ row, onActivate }: { row: InventoryRow; onActivate?: (variantId: string) => void }) {
  const label = row.title ?? row.article ?? 'Изделие'
  const action = () => { if (row.productId !== null) onActivate?.(row.productId) }
  return <div className="grid w-full grid-cols-[56px_minmax(180px,1.8fr)_minmax(90px,.8fr)_minmax(90px,.8fr)_minmax(150px,1.2fr)_minmax(90px,.7fr)] items-center gap-3 border-t border-rule/60 px-4 py-2 text-left text-xs hover:bg-gold/10">
    <button type="button" className="col-span-5 grid min-w-0 grid-cols-[56px_minmax(180px,1.8fr)_minmax(90px,.8fr)_minmax(90px,.8fr)_minmax(150px,1.2fr)] items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold" onClick={action} disabled={row.productId === null} aria-label={`Открыть изделие: ${label}`}>
      <WarehouseThumbnail mediaUrl={row.primaryMediaUrl} label={`Фото ${label}`} />
      <span className="min-w-0 truncate font-semibold">{label}</span>
      <span>{row.techSize ?? '—'}</span>
      {pieceWeight(row)}
      <span data-piece-status={row.stockState}>{inventoryStockStateLabels[row.stockState]}</span>
    </button>
    <ProblemsCell problems={row.problems ?? []} problemsAvailable={row.problemsAvailable} />
  </div>
}

function OrderUnlinkedLine({ line }: { line: InventoryGroupChildren['unlinkedLines'][number] }) {
  return <div className="grid grid-cols-[56px_minmax(180px,1.8fr)_minmax(90px,.8fr)_minmax(90px,.8fr)_minmax(150px,1.2fr)] items-center gap-3 border-t border-rule/60 bg-bone/30 px-4 py-2 text-xs" data-unlinked-line={line.position}>
    <span className="text-ink-muted">Строка</span>
    <span className="min-w-0 truncate">{line.name}</span>
    <span>×{line.quantity}</span>
    <span className="tabular-nums">{formatRubleAmount(line.amount)}</span>
    <span className="text-ink-muted">Изделие не связано</span>
  </div>
}

function UnitLeaf({ unit, onActivate, selection }: { unit: InventoryGroupUnit; onActivate: (unit: InventoryGroupUnit) => void; selection?: WarehouseSelection }) {
  const identity = unit.uin
    ? `УИН ${unit.uin}`
    : `УИН недоступен: ${reasonText(unit.uinUnavailableReason ?? 'unit.marking_not_recorded_in_source')} · серия ${unit.unitSeries}`
  return <div className="flex w-full items-center gap-3 border-t border-rule/60 px-4 py-2 text-left text-[11px] text-ink-2 hover:bg-gold/10">{selection ? <input type="checkbox" checked={selection.isSelected(unit.productId)} onChange={() => selection.toggle(unit.productId)} aria-label={`Выбрать ${identity}`} /> : null}<button type="button" data-unit-id={unit.productId} aria-label={`Открыть быстрый просмотр: ${identity}`} onClick={(event) => { event.stopPropagation(); onActivate(unit) }} className="grid min-w-0 flex-1 grid-cols-4 gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"><ValueOrReason value={unit.uin} reason={unit.uinUnavailableReason ?? 'unit.marking_not_recorded_in_source'} /><span>{unit.techSize ?? 'Без размера'}</span><span>{weightValue(unit.weightGrams, unit.weightUnavailableReason)}</span><span>{unit.unitSeries}</span></button><ProblemsCell problems={unit.problems ?? []} problemsAvailable={unit.problemsAvailable} /></div>
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(
    'a[href],button,input,select,textarea,summary,[role="button"],[role="link"]',
  ))
}

function Chevron({ expanded }: { expanded: boolean }) {
  return <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" className={`h-5 w-5 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}><path d="m9 18 6-6-6-6" /></svg>
}

function GroupLine({ row, filters, onUnitActivate, onVariantActivate, onOrderActivate, visibleColumns, selection }: { row: InventoryGroupRow; filters: InventoryGroupFilters; onUnitActivate: (unit: InventoryGroupUnit) => void; onVariantActivate?: (variantId: string) => void; onOrderActivate?: (row: InventoryGroupRow) => void; visibleColumns?: readonly InventoryColumnId[]; selection?: WarehouseSelection }) {
  const [expanded, setExpanded] = useState(false)
  const [children, setChildren] = useState<InventoryGroupChildren | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [limit, setLimit] = useState(row.childLevel === 'unit' ? 3 : 5)
  const expandable = row.childCount > 0
  const isOrder = row.level === 'order'
  const selectedOrderColumns = isOrder ? orderColumns(visibleColumns) : []
  const directProducts = directProductParent(row)
  // A size row with no pieces is terminal, but its Product card is still a
  // first-class child. Load that card without adding an expansion affordance.
  const loadTerminalProducts = !expandable && row.level === 'articleSize' && row.variantCount > 0
  const loadChildren = expanded || loadTerminalProducts

  // The parent keeps the same keyed GroupLine while a background request
  // replaces its page. Compare the query's value rather than its object
  // identity so an equivalent refresh does not throw away the open subtree.
  const filterSignature = JSON.stringify(filters)
  const childrenId = `warehouse-group-children-${encodeURIComponent(row.groupKey)}`

  useEffect(() => {
    setChildren(null)
    setError(null)
    setLimit(row.childLevel === 'unit' ? 3 : 5)
  }, [filterSignature, row.childCount, row.childLevel, row.groupKey, row.variantCount])
  useEffect(() => {
    if (!loadChildren || children || error) return
    const controller = new AbortController()
    getInventoryGroupChildren(row.groupKey, { ...filters, limit, offset: 0 }, controller.signal)
      .then(setChildren)
      .catch((reason: unknown) => { if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Не удалось загрузить склад') })
    return () => controller.abort()
  }, [children, error, filterSignature, limit, loadChildren, row.groupKey])

  const article = row.article ?? row.modelArticle ?? 'Без артикула'
  const rowLabel = isOrder
    ? orderRowLabel(row)
    : expandable
    ? `${expanded ? 'Скрыть' : 'Показать'} ${childActionPopulationCount(row)}: ${article}${row.techSize ? ` · ${row.techSize}` : ''}`
    : `${childPopulationCount(row)}: ${article}${row.techSize ? ` · ${row.techSize}` : ''}`
  const groupRowClassName = isOrder
    ? 'grid min-w-[1140px] grid-cols-[44px_repeat(9,minmax(140px,1fr))]'
    : selection
    ? 'grid min-w-[1110px] grid-cols-[44px_44px_1.2fr_1.5fr_1fr_.7fr_1fr_.7fr]'
    : 'grid min-w-[1070px] grid-cols-[44px_1.2fr_1.5fr_1fr_.7fr_1fr_.7fr]'
  const variants = children === null
    ? []
    : Array.from(new Map(children.variants.map((variant) => [variant.productId, variant])).values())
  // An expanded group selects the pieces it shows; a closed one has none loaded to select.
  const leafIds = expanded && children !== null
    ? [...new Set([...variants.map((variant) => variant.productId), ...(directProducts ? [] : children.units.map((unit) => unit.productId))])]
    : []
  const toggle = () => {
    if (expandable) setExpanded((value) => !value)
  }
  const toggleFromRow = (event: MouseEvent<HTMLDivElement>) => {
    if (!expandable || isInteractiveTarget(event.target)) return
    event.stopPropagation()
    event.currentTarget.focus()
    toggle()
  }
  const toggleFromKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!expandable || isInteractiveTarget(event.target)) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.focus()
    toggle()
  }
  const activateOrder = () => {
    if (isOrder) onOrderActivate?.(row)
  }
  const orderKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOrder) return
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      activateOrder()
      return
    }
    if (event.key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      toggle()
    }
  }

  return (
    <div className="border-b border-rule/70 last:border-b-0" data-group-key={row.groupKey} data-group-level={row.level}>
      <div role="row" tabIndex={isOrder || expandable ? 0 : undefined} aria-expanded={expandable ? expanded : undefined} aria-controls={expandable ? childrenId : undefined} aria-label={rowLabel} onClick={isOrder ? activateOrder : toggleFromRow} onKeyDown={isOrder ? orderKeyboard : toggleFromKeyboard} className={`${groupRowClassName}${isOrder || expandable ? ' cursor-pointer' : ''} items-center gap-2 px-3 py-3 text-xs outline-none transition-colors hover:bg-bone/45 focus-visible:bg-gold/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold-deep`}>
        {isOrder ? <>
          {expandable ? <button type="button" aria-expanded={expanded} aria-controls={childrenId} aria-label={expanded ? `Скрыть состав заказа №${row.orderNumber ?? ''}` : `Показать состав заказа №${row.orderNumber ?? ''}`} onClick={(event) => { event.stopPropagation(); toggle() }} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gold-deep transition-colors hover:bg-gold/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep"><Chevron expanded={expanded} /></button> : <span aria-hidden="true" className="h-9 w-9" />}
          {selectedOrderColumns.map((column) => <span key={column.id} className="min-w-0 truncate">{column.groupCell?.(row)}</span>)}
          <span>{groupProblems(row)}</span>
        </> : <>
          {selection ? <input
            type="checkbox"
            disabled={leafIds.length === 0}
            checked={leafIds.length > 0 && leafIds.every((productId) => selection.isSelected(productId))}
            onChange={() => selection.togglePage(leafIds)}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Выбрать изделия группы ${article}`}
            title={leafIds.length === 0 ? 'Раскройте группу, чтобы выбрать изделия' : `Выбрать показанные изделия группы: ${leafIds.length}`}
          /> : null}
          {expandable ? <button type="button" aria-expanded={expanded} aria-controls={childrenId} aria-label={rowLabel} onClick={(event) => { event.stopPropagation(); toggle() }} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gold-deep transition-colors hover:bg-gold/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep"><Chevron expanded={expanded} /></button> : <span aria-hidden="true" className="h-9 w-9" />}
          <span className="font-semibold">{article}{row.techSize ? ` · ${row.techSize}` : ''}</span>
          <span><ValueOrReason value={row.title} reason={null} /></span>
          <span><b>{weightValue(row.weight.averageGrams, row.weight.unavailableReason)}</b>{row.weight.averageGrams !== null ? <small className="block text-ink-muted">{grams(row.weight.minGrams)}–{grams(row.weight.maxGrams)}</small> : null}</span>
          <span><ValueOrReason value={row.inStockQuantity === null ? null : productCount(row.inStockQuantity)} reason={row.inStockUnavailableReason ?? 'group.reason_not_published'} />{stockConflictLine(row.stockConflict)}</span>
          <span>{groupMediaCounts(row)}</span>
          <span>{groupProblems(row)}</span>
        </>}
      </div>
      <div id={childrenId} hidden={!loadChildren} aria-hidden={!loadChildren} className="ml-8 border-l border-rule bg-bone/20" data-group-children={row.groupKey}>
        {error ? <p role="alert" className="px-4 py-2 text-xs text-st-unpaid">{error}</p> : null}
        {!children && !error ? <p className="px-4 py-2 text-xs text-ink-muted">Загружаем…</p> : null}
        {isOrder ? <>
          {children ? <div className="grid grid-cols-[56px_minmax(180px,1.8fr)_minmax(90px,.8fr)_minmax(90px,.8fr)_minmax(150px,1.2fr)_minmax(90px,.7fr)] gap-3 px-4 py-2 text-[10px] uppercase text-ink-muted"><span>Фото</span><span>Изделие</span><span>Размер</span><span>Вес</span><span>Статус изделия</span><span>Проблемы</span></div> : null}
          {children?.rows.map((piece) => <OrderPieceRow key={piece.rowKey} row={piece} onActivate={onVariantActivate} />)}
          {children?.unlinkedLines.map((line) => <OrderUnlinkedLine key={line.position} line={line} />)}
        </> : <>
          {children?.groups.map((child) => <GroupLine key={child.groupKey} row={child} filters={filters} onUnitActivate={onUnitActivate} onVariantActivate={onVariantActivate} onOrderActivate={onOrderActivate} visibleColumns={visibleColumns} selection={selection} />)}
        </>}
        {!isOrder && variants.map((variant) => {
          const label = `${variant.title ?? article}${variant.techSize ? ` · ${variant.techSize}` : ''}`
          return <div key={variant.productId} className="flex w-full items-center gap-3 border-t border-rule/60 px-4 py-2 text-left text-xs hover:bg-gold/10">
            {selection ? <input type="checkbox" checked={selection.isSelected(variant.productId)} onChange={() => selection.toggle(variant.productId)} aria-label={`Выбрать ${label}`} /> : null}
            <button type="button" aria-label={`Открыть товар: ${label}`} disabled={!onVariantActivate} onClick={(event) => { event.stopPropagation(); onVariantActivate?.(variant.productId) }} className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold">
            <WarehouseThumbnail mediaUrl={variant.primaryMediaUrl} label={`Фото ${label}`} />
            <span><span className="block font-semibold">{label}</span><span className="text-ink-muted">Товар · открыть</span></span>
            </button>
            <ProblemsCell problems={variant.problems ?? []} problemsAvailable={variant.problemsAvailable} />
          </div>
        })}
        {!isOrder && children && children.total === 0 && children.variantTotal === 0 ? <p role="status" className="px-4 py-2 text-xs text-ink-muted">{children.childLevel === 'unit' ? 'Физические экземпляры по выбранным условиям не зарегистрированы. Товар не подтверждает наличие на складе.' : `В этой группе нет дочерних ${childLevelNouns[children.childLevel][2]} по выбранным условиям.`}</p> : null}
        {!isOrder && !directProducts ? children?.units.map((unit) => <UnitLeaf key={unit.productId} unit={unit} onActivate={onUnitActivate} selection={selection} />) : null}
        {children && children.variantTotal > children.variants.length ? <button type="button" className="px-4 py-2 text-xs text-gold-deep" onClick={(event) => { event.stopPropagation(); setLimit(directProducts ? children.variantTotal : Math.max(children.total, children.variantTotal)); setChildren(null) }}>Показать все товары: {children.variantTotal}</button> : null}
        {isOrder && children && children.total > children.rows.length ? <button type="button" className="px-4 py-2 text-xs text-gold-deep" onClick={(event) => { event.stopPropagation(); setLimit(children.total); setChildren(null) }}>Показать все изделия: {children.total}</button> : null}
        {!isOrder && !directProducts && children && children.total > children.groups.length + children.units.length ? <button type="button" className="px-4 py-2 text-xs text-gold-deep" onClick={(event) => { event.stopPropagation(); setLimit(children.total); setChildren(null) }}>Показать все {actionPopulationCount(children.total, children.childLevel)}</button> : null}
      </div>
    </div>
  )
}

export function WarehouseGroupedTable({ page, filters, loading, error, onUnitActivate, onVariantActivate, onOrderActivate, onPopulationRowActivate, selectedRowKey, sort, onSortChange, visibleColumns, selection, selectionTotal = 0, onSelectAll }: { page: InventoryGroupPage | null; filters: InventoryGroupFilters; loading: boolean; error: string | null; onUnitActivate: (unit: InventoryGroupUnit) => void; onVariantActivate?: (variantId: string) => void; onOrderActivate?: (row: InventoryGroupRow) => void; onPopulationRowActivate?: (row: InventoryRow) => void; selectedRowKey?: string | null; sort?: WarehouseSort; onSortChange?: (sort: WarehouseSort) => void; visibleColumns?: readonly InventoryColumnId[]; selection?: WarehouseSelection; selectionTotal?: number; onSelectAll?: () => Promise<void> }) {
  return <div className="overflow-x-auto rounded-lg border border-rule bg-paper">
    <div role="table" aria-label="Группы склада">
      {page?.level === 'order' ? <div role="row" className="grid min-w-[1140px] grid-cols-[44px_repeat(9,minmax(140px,1fr))] gap-2 border-b border-rule bg-bone/60 px-3 py-3 text-[10px] uppercase text-ink-muted"><span /><span>Заказ</span><span>Создан</span><span>Покупатель</span><span>Статус заказа</span><span>Программа</span><span>Город</span><span>Сумма</span><span>Состав</span><span>Проблемы</span></div> : <div role="row" className={`grid ${selection ? 'min-w-[1110px] grid-cols-[44px_44px_1.2fr_1.5fr_1fr_.7fr_1fr_.7fr]' : 'min-w-[1070px] grid-cols-[44px_1.2fr_1.5fr_1fr_.7fr_1fr_.7fr]'} gap-2 border-b border-rule bg-bone/60 px-3 py-3 text-[10px] uppercase text-ink-muted`}>{selection ? <span><SelectAllCheckbox selection={selection} total={selectionTotal} onSelectAll={onSelectAll} /></span> : null}<span /><span>Артикул</span><span>Изделие</span><span>Вес, г</span><span>На складе</span><span>Медиа</span><span>Проблемы</span></div>}
      {error ? <p role="alert" className="p-8 text-center text-st-unpaid">{error}</p> : null}
      {loading && !page ? <p className="p-8 text-center text-ink-muted">Загружаем…</p> : null}
      {page?.items.map((row) => <GroupLine key={row.groupKey} row={row} filters={filters} onUnitActivate={onUnitActivate} onVariantActivate={onVariantActivate} onOrderActivate={onOrderActivate} visibleColumns={visibleColumns} selection={selection} />)}
    </div>
     {page && page.populationRows.length > 0 && onPopulationRowActivate && sort && onSortChange ? <section className="mt-3" aria-label="Сверка источников"><h2 className="px-3 py-2 text-xs font-semibold text-ink-2">Записи источников вне групп · {page.populationRowTotal.toLocaleString('ru-RU')}</h2><WarehouseLeafTable rows={page.populationRows} loading={loading} error={null} onRowActivate={onPopulationRowActivate} selectedRowKey={selectedRowKey} sort={sort} onSortChange={onSortChange} reconciliationMode /></section> : null}
  </div>
}

function leafQuantity(row: InventoryRow): ReactNode {
  // A row owns one piece, so the register number beside it is source evidence
  // about that piece and never its possession. It is drawn as the register's
  // own sentence when the piece has no accepted quantity of its own.
  if (row.rowGrain === 'piece' && row.quantity === null && row.aggregateQuantity !== null) {
    return <span data-stock-state={row.aggregateStockState}>Сводка 1С: {row.aggregateQuantity} · по этому изделию не подтверждено</span>
  }
  if (row.quantity !== null) return <span data-stock-state={row.stockState}>Источник сообщил: {row.quantity} · {inventoryStockStateLabels[row.stockState]}</span>
  return <span data-stock-state={row.stockState}>{inventoryStockStateLabels[row.stockState]}</span>
}

/** The server's leaf union: pieces replace their parent variant rows. */
export function WarehouseLeafTable({
  rows,
  loading,
  error,
  onRowActivate,
  selectedRowKey,
  sort,
  onSortChange,
  reconciliationMode = false,
}: {
  rows: readonly InventoryRow[]
  loading: boolean
  error: string | null
  onRowActivate: (row: InventoryRow) => void
  selectedRowKey?: string | null
  sort: WarehouseSort
  onSortChange: (sort: WarehouseSort) => void
  reconciliationMode?: boolean
}) {
  // The rows arrive in the order the server computed over the whole population,
  // so they are rendered as delivered. Re-sorting them here would reorder one
  // page of a population instead of ordering the population, and the header
  // would then disagree with the page a second request returns.
  const toggleSort = (column: WarehouseSortColumn) => onSortChange({
    column,
    direction: sort.column === column && sort.direction === 'ascending' ? 'descending' : 'ascending',
  })
  const sortButton = (column: WarehouseSortColumn, label: string) => <button type="button" className="text-left" onClick={() => toggleSort(column)}>{label}{sort.column === column ? (sort.direction === 'ascending' ? ' ↑' : ' ↓') : null}</button>
  const selectedRow = selectedRowKey === null || selectedRowKey === undefined ? null : rows.find((row) => row.rowKey === selectedRowKey) ?? null
  const selectedUnresolvedRow = selectedRow !== null && selectedRow.rowGrain !== 'piece' ? selectedRow : null
  const selectionEvidenceId = 'warehouse-selected-evidence'
  const tableLabel = reconciliationMode ? 'Сверка источников' : 'Товары на складе'
  const rowAction = reconciliationMode ? 'Открыть запись источника' : 'Открыть товар'
  const renderRow = (row: InventoryRow): ReactNode => {
    const article = `${row.article ?? 'Без артикула'}${row.techSize ? ` · ${row.techSize}` : ''}`
    const identity = <ValueOrReason value={row.uin} reason={row.uinUnavailableReason} />
    const physical = row.rowGrain === 'piece'
    const aggregateSource = physical
      ? row.aggregateStockAuthority
      : row.stockAuthority
    const aggregateFreshness = physical ? row.aggregateFreshness : row.freshness
    const content = <div className="col-span-8 grid grid-cols-[72px_1.1fr_1.8fr_1.2fr_1.2fr_1fr_1.3fr_1.4fr] items-center gap-2 text-xs">
      <WarehouseThumbnail mediaUrl={row.primaryMediaUrl} label={`Фото ${article}`} />
      <span className="font-medium" data-row-grain={row.rowGrain}>{inventoryRowGrainLabels[row.rowGrain]}</span>
      <span><ValueOrReason value={row.title} reason={row.titleUnavailableReason} /></span>
      <span>{article}</span>
      <span>{reconciliationMode
        ? (row.unresolvedId ?? 'Идентификатор не записан')
        : physical ? identity : 'Не применимо'}</span>
      <span className="tabular-nums">{leafQuantity(row)}</span>
      <span data-review-state={row.reviewState} data-reason={reconciliationMode ? row.unresolvedReason ?? undefined : undefined}>{reconciliationMode
        ? (row.unresolvedReason ? reasonText(row.unresolvedReason) : inventoryReviewStateLabels[row.reviewState])
        : inventoryReviewStateLabels[row.reviewState]}</span>
      <span>{reconciliationMode
        ? `${row.sourceAccountCode ?? 'Источник не записан'}${row.stockObservedAt ? ` · ${warehouseDateTime(row.stockObservedAt)}` : ''}`
        : aggregateSource
          ? `${aggregateSource}${aggregateFreshness ? ` · ${freshnessLabels[aggregateFreshness]}` : ''}${physical && row.aggregateSourceAccountCount > 1 ? ` · ${row.aggregateSourceAccountCount} аккаунта` : ''}`
          : row.unresolvedReason ? reasonText(row.unresolvedReason) : 'Каноническая запись'}</span>
    </div>
    const selected = row.rowKey === selectedRowKey
    return <div key={row.rowKey} role="row" className={`grid min-w-[1200px] grid-cols-[72px_1.1fr_1.8fr_1.2fr_1.2fr_1fr_1.3fr_1.4fr_64px] items-center gap-2 border-b border-rule/70 px-3 py-3 text-xs ${selected ? 'bg-gold/10' : ''}`} data-row-selected={selected ? 'true' : undefined} aria-selected={selected ? true : undefined}>
      <button type="button" className="col-span-8 grid min-w-0 text-left hover:bg-gold/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold-deep" data-row-key={row.rowKey} data-row-selected={selected ? 'true' : undefined} aria-selected={selected ? true : undefined} onClick={() => onRowActivate(row)} aria-describedby={selected ? selectionEvidenceId : undefined} aria-label={`${rowAction}: ${row.title ?? article}`}>{content}</button>
      <ProblemsCell problems={row.problems ?? []} problemsAvailable={row.problemsAvailable} />
    </div>
  }
  return <div className="overflow-x-auto rounded-lg border border-rule bg-paper">
    <div role="table" aria-label={tableLabel}>
      <div role="row" className="grid min-w-[1200px] grid-cols-[72px_1.1fr_1.8fr_1.2fr_1.2fr_1fr_1.3fr_1.4fr_64px] gap-2 border-b border-rule bg-bone/60 px-3 py-3 text-[10px] uppercase text-ink-muted"><span>Фото</span><span>Тип записи</span><span>{sortButton('title', 'Изделие')}</span><span>{sortButton('article', 'Артикул · размер')}</span><span>{sortButton('uin', reconciliationMode ? 'Идентификатор источника' : 'Маркировка')}</span><span>{reconciliationMode ? 'Сведения источника' : 'Состояние'}</span><span>{reconciliationMode ? 'Причина' : 'Проверка'}</span><span>{reconciliationMode ? 'Источник' : 'Источник факта'}</span><span>Проблемы</span></div>
      {error ? <p role="alert" className="p-8 text-center text-st-unpaid">{error}</p> : null}
      {loading && rows.length === 0 ? <p className="p-8 text-center text-ink-muted">Загружаем…</p> : null}
      {rows.map(renderRow)}
      {!loading && !error && rows.length === 0 ? <p className="p-8 text-center text-ink-muted">По заданным фильтрам популяций нет.</p> : null}
    </div>
    {selectedUnresolvedRow ? <section id={selectionEvidenceId} role="status" aria-label="Выбранная запись источника" className="border-t border-gold/40 bg-gold/5 px-4 py-3 text-xs text-ink-2"><p className="font-semibold">Выбрана запись источника · изделие не определено</p><p className="mt-1">Идентификатор свидетельства: {selectedUnresolvedRow.unresolvedId ?? 'не записан'}</p><p className="mt-1">Источник: {selectedUnresolvedRow.sourceAccountCode ?? 'не записан'}</p><p className="mt-1">Причина: {selectedUnresolvedRow.unresolvedReason ? reasonText(selectedUnresolvedRow.unresolvedReason) : 'не записана'}</p></section> : null}
  </div>
}
